import { memo, useMemo, useState } from "react";
import { AlarmClockOff, Clock3, Database, Hourglass, TimerOff, UserX, Users } from "lucide-react";

import { usePageBack } from "../../context/PageBackContext.js";
import { cn } from "../../lib/utils.js";
import { reportAnchor, reportRange, shiftReportAnchor, REPORT_PERIOD_OPTIONS, type ReportPeriod } from "../../lib/reportRange.js";
import { SummaryStatSuffix } from "../../components/SummaryStatCard.js";
import { DropdownMultiSelect, DropdownSelect } from "../../components/DropdownButton.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { SpecularButton } from "../../components/ui/specular-button.js";
import { StatusChip, type StatusChipProps } from "../../components/ui/status-chip.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs.js";
import type { PaginationMeta } from "../../components/ui/pagination.js";
import {
  ReportPage, FilterBar, ErrorBanner, OrgFilters, ExportButton, CardGrid, ReportStatCard, ReportTableFrame, ReportFooter, PageSizeSelect,
  TableScrollStart, TableToolbar, dropdownProps, splitList, joinList, stickyName, stickyCode, NAME_WIDTH, CODE_WIDTH, type OpenState,
} from "../../components/reports/ReportKit.js";
import {
  useUrlFilters, useDebouncedSearch, useReport, useOrgMeta, toParams, downloadReport,
} from "../../components/reports/useReport.js";

type HoursTone = "present" | "short" | "incomplete" | "absent" | "leave" | "halfDay" | "holiday" | "off" | "none";
interface HoursCell { label: string; tone: HoursTone; hours?: number; t?: string }
type AttTone = "approved" | "unapproved" | "absent" | "leave" | "holiday" | "off" | "none";
interface AttCell { label: string; tone: AttTone; t?: string }
interface TimesheetRow {
  id: string; name: string; code: string; department: string | null; office: string | null;
  cells: HoursCell[];
  attendance: AttCell[]; approvedDays: number; unapprovedDays: number;
  totalHours: number; measuredDays: number; presentDays: number; avgHours: number; shiftHours: number;
  shortDays: number; absentDays: number; awaitingDays: number; leaveDays: number; missingCheckouts: number; belowShift: boolean;
}
interface TimesheetReport {
  from: string; to: string; granularity: "day" | "month"; columns: string[];
  rows: TimesheetRow[];
  /** Loaded by a separate request (`part=cards`), so a page turn never refetches it. */
  cards?: {
    employees: number; totalHours: number; avgHours: number; belowShift: number;
    missingCheckouts: number; absentDays: number; biometricUpTo: string | null; awaitingDays: number; expectedBasis: string;
  };
  meta: PaginationMeta;
}

const DEFAULTS = {
  search: "", officeIds: "", departmentIds: "", period: "month", anchor: "",
  issues: "", view: "attendance", page: "1", limit: "25",
};
// The cards cover everyone in scope; the issue filters narrow only the table.
const ROW_ONLY = ["belowShift", "missingCheckout"];

const API = "/reports/hr/timesheet";

const ISSUE_OPTIONS = [
  { value: "belowShift", label: "Below shift hours" },
  { value: "missingCheckout", label: "Missing check-out" },
];

const CHIP_TONE: Record<HoursTone, StatusChipProps["tone"]> = {
  present: "present", short: "warning", incomplete: "warning", absent: "absent",
  leave: "leave", halfDay: "halfDay", holiday: "holiday", off: "off", none: "none",
};

const ATT_CHIP: Record<AttTone, StatusChipProps["tone"]> = {
  approved: "present", unapproved: "warning", absent: "absent", leave: "leave", holiday: "holiday", off: "off", none: "none",
};

const ATT_LEGEND: { tone: StatusChipProps["tone"]; label: string; text: string }[] = [
  { tone: "present", label: "P", text: "Present · approved" },
  { tone: "warning", label: "P", text: "Present · manager not approved" },
  { tone: "absent", label: "A", text: "Absent" },
  { tone: "leave", label: "SI", text: "Leave" },
  { tone: "holiday", label: "H", text: "Holiday" },
  { tone: "off", label: "", text: "Weekend" },
  { tone: "none", label: "·", text: "Not uploaded yet" },
];

const LEGEND: { tone: StatusChipProps["tone"]; label: string; text: string }[] = [
  { tone: "present", label: "9", text: "Met shift" },
  { tone: "warning", label: "7.5", text: "Short / no check-out" },
  { tone: "absent", label: "A", text: "Absent" },
  { tone: "leave", label: "SI", text: "Leave" },
  { tone: "halfDay", label: "½", text: "Half day" },
  { tone: "holiday", label: "H", text: "Holiday" },
  { tone: "off", label: "", text: "Weekend" },
  { tone: "none", label: "·", text: "Not uploaded yet" },
];

const DOW = ["S", "M", "T", "W", "T", "F", "S"];
const MONTH_FMT = new Intl.DateTimeFormat("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });

/** One employee's row. Memoised: a month is ~31 chips per row, 25-100 rows a page. */
const HoursRow = memo(function HoursRow({ row, byMonth }: { row: TimesheetRow; byMonth: boolean }) {
  return (
    <TableRow className="group hover:bg-surface-50">
      <TableCell className={cn(stickyName, "bg-surface-0 group-hover:bg-surface-50")}>
        <div className={cn(NAME_WIDTH, "truncate font-semibold text-surface-900")} title={[row.name, row.department, row.office].filter(Boolean).join(" · ")}>{row.name}</div>
      </TableCell>
      <TableCell className={cn(stickyCode, "bg-surface-0 text-surface-600 group-hover:bg-surface-50")}>
        <div className={cn(CODE_WIDTH, "truncate")}>{row.code}</div>
      </TableCell>
      {row.cells.map((c, i) => (
        <TableCell key={i} className={cn("px-0.5 py-1 text-center", byMonth ? "w-16" : "w-10")}>
          {byMonth ? (
            <span className="text-[12px] font-semibold tabular-nums text-surface-800" title={c.t}>{c.label || "—"}</span>
          ) : (
            <StatusChip tone={CHIP_TONE[c.tone]} size="wide" title={c.t}>{c.label}</StatusChip>
          )}
        </TableCell>
      ))}
      <TableCell className="border-l border-surface-200 px-2 text-center font-bold tabular-nums">{row.totalHours.toFixed(1)}</TableCell>
      <TableCell className="px-2 text-center tabular-nums">{row.presentDays}</TableCell>
      <TableCell className={cn("px-2 text-center font-semibold tabular-nums", row.measuredDays > 0 && (row.belowShift ? "text-[var(--color-warning-fg)]" : "text-[var(--color-success-fg)]"))}
        title={`Shift ${row.shiftHours} hrs`}>
        {row.measuredDays > 0 ? row.avgHours.toFixed(2) : "—"}
      </TableCell>
      <TableCell className={cn("px-2 text-center tabular-nums", row.shortDays > 0 && "font-semibold text-[var(--color-warning-fg)]")}>{row.shortDays}</TableCell>
      <TableCell className={cn("px-2 text-center tabular-nums", row.absentDays > 0 && "font-semibold text-[var(--color-error-fg)]")}>{row.absentDays}</TableCell>
      <TableCell className="px-2 text-center tabular-nums">{row.leaveDays}</TableCell>
      <TableCell className={cn("px-2 text-center tabular-nums", row.missingCheckouts > 0 && "font-semibold text-[var(--color-warning-fg)]")}>{row.missingCheckouts}</TableCell>
    </TableRow>
  );
});

/** One employee's Attendance row: P / A per day, P green once the manager approved the week. */
const AttendanceRow = memo(function AttendanceRow({ row, byMonth }: { row: TimesheetRow; byMonth: boolean }) {
  return (
    <TableRow className="group hover:bg-surface-50">
      <TableCell className={cn(stickyName, "bg-surface-0 group-hover:bg-surface-50")}>
        <div className={cn(NAME_WIDTH, "truncate font-semibold text-surface-900")} title={[row.name, row.department, row.office].filter(Boolean).join(" · ")}>{row.name}</div>
      </TableCell>
      <TableCell className={cn(stickyCode, "bg-surface-0 text-surface-600 group-hover:bg-surface-50")}>
        <div className={cn(CODE_WIDTH, "truncate")}>{row.code}</div>
      </TableCell>
      {row.attendance.map((a, i) => (
        <TableCell key={i} className={cn("px-0.5 py-1 text-center", byMonth ? "w-16" : "w-10")}>
          <StatusChip tone={ATT_CHIP[a.tone]} size="wide" title={a.t}>{a.label}</StatusChip>
        </TableCell>
      ))}
      <TableCell className="border-l border-surface-200 px-2 text-center font-bold tabular-nums">{row.presentDays}</TableCell>
      <TableCell className={cn("px-2 text-center tabular-nums", row.approvedDays > 0 && "text-[var(--color-success-fg)]")}>{row.approvedDays}</TableCell>
      <TableCell className={cn("px-2 text-center tabular-nums", row.unapprovedDays > 0 && "font-semibold text-[var(--color-warning-fg)]")}>{row.unapprovedDays}</TableCell>
      <TableCell className={cn("px-2 text-center tabular-nums", row.absentDays > 0 && "font-semibold text-[var(--color-error-fg)]")}>{row.absentDays}</TableCell>
      <TableCell className="px-2 text-center tabular-nums">{row.leaveDays}</TableCell>
    </TableRow>
  );
});

export default function TimesheetReportPage() {
  usePageBack("/dashboard");
  const [f, setF] = useUrlFilters(DEFAULTS);
  const [search, setSearch] = useDebouncedSearch(f.search, (v) => setF({ search: v }));
  const [open, setOpen] = useState<string | null>(null);
  const openState: OpenState = { open, setOpen };
  const org = useOrgMeta();

  const period = (REPORT_PERIOD_OPTIONS.some((o) => o.value === f.period) ? f.period : "month") as ReportPeriod;
  const anchor = f.anchor || reportAnchor(period);
  const range = reportRange(period, anchor);

  const params = useMemo(() => {
    // `view` only picks the tab; both tabs come in the same response.
    const { period: _p, anchor: _a, issues, view: _v, ...rest } = f;
    const picked = splitList(issues);
    return toParams(rest, {
      from: range.from, to: range.to,
      ...(picked.includes("belowShift") ? { belowShift: "true" } : {}),
      ...(picked.includes("missingCheckout") ? { missingCheckout: "true" } : {}),
    });
  }, [f, range.from, range.to]);
  const { data, cards: c, loading, error } = useReport<TimesheetReport>(API, params, "TimesheetReportPage", ROW_ONLY);

  const rows = data?.rows ?? [];
  const byMonth = data?.granularity === "month";
  const view = f.view === "hours" ? "hours" : "attendance";
  const legend = view === "attendance" ? ATT_LEGEND : LEGEND;

  return (
    <ReportPage
      title="Timesheet Reports"
      description="Attendance (present / absent, with the manager's approval) and hours worked per day, from biometric attendance."
    >
      <div className="flex flex-col gap-3">
        <CardGrid>
          <ReportStatCard icon={Users} tone="brand" label="Employees" value={c?.employees ?? "—"} />
          <ReportStatCard icon={Clock3} tone="success" label="Total Hours" value={<>{c?.totalHours ?? "—"}<SummaryStatSuffix> hrs</SummaryStatSuffix></>} caption={range.label} />
          <ReportStatCard icon={Hourglass} tone="info" label="Avg / Day" value={<>{c?.avgHours ?? "—"}<SummaryStatSuffix> hrs</SummaryStatSuffix></>} caption="Days with both punches" />
          <ReportStatCard icon={TimerOff} tone="warning" label="Below Expected" value={c?.belowShift ?? "—"} caption={c?.expectedBasis ?? "Average under expected hours"} />
          <ReportStatCard icon={AlarmClockOff} tone="rose" label="No Check-out" value={c?.missingCheckouts ?? "—"} caption="Past days never punched out" />
          <ReportStatCard icon={UserX} tone="danger" label="Absent Days" value={c?.absentDays ?? "—"} caption={c?.awaitingDays ? `${c.awaitingDays} days awaiting upload not counted` : undefined} />
          <ReportStatCard icon={Database} tone="cyan" label="Biometric Data" value={c?.biometricUpTo ?? "—"} caption={c?.biometricUpTo ? "Later days show · until uploaded" : "No biometric upload yet"} valueClassName="text-base" />
        </CardGrid>
      </div>

      <FilterBar>
        <SearchInput className="w-56" placeholder="Search name or code" value={search} onChange={(e) => setSearch(e.target.value)} />
        <OrgFilters meta={org} officeIds={f.officeIds} departmentIds={f.departmentIds} onChange={setF} openState={openState} />
        <DropdownSelect
          value={period} onChange={(v) => setF({ period: v, anchor: "" })} options={REPORT_PERIOD_OPTIONS}
          placeholder="Period" ariaLabel="Period" clearable={false} {...dropdownProps("period", openState)}
        />
        <div className="flex items-center gap-3">
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor(period, anchor, -1) })} aria-label="Previous period" className="w-8 px-0">&larr;</SpecularButton>
          <div className="min-w-[150px] text-center text-sm font-semibold">{range.label}</div>
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor(period, anchor, 1) })} aria-label="Next period" className="w-8 px-0">&rarr;</SpecularButton>
        </div>
        <DropdownMultiSelect
          values={splitList(f.issues)} onChange={(v) => setF({ issues: joinList(v) })} options={ISSUE_OPTIONS}
          allLabel="All attendance" ariaLabel="Attendance issues" {...dropdownProps("issues", openState)}
        />
      </FilterBar>

      <Tabs value={view} onValueChange={(v) => setF({ view: v })}>
        <TabsList>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="hours">Hours</TabsTrigger>
        </TabsList>
      </Tabs>

      <TableToolbar
        left={(!byMonth || view === "attendance") && (
          <div className="flex flex-wrap items-center gap-3 text-[12px] text-surface-600">
            {legend.map((l) => (
              <span key={l.text} className="flex items-center gap-1.5">
                <StatusChip tone={l.tone} size="wide">{l.label}</StatusChip>{l.text}
              </span>
            ))}
          </div>
        )}
      >
        <PageSizeSelect limit={f.limit} onLimit={(v) => setF({ limit: v })} openState={openState} />
        <ExportButton onExport={() => downloadReport(API, params, `timesheet-${range.from}-to-${range.to}.xlsx`)} />
      </TableToolbar>

      <ErrorBanner message={error} />

      <ReportTableFrame loading={loading} empty={rows.length === 0} emptyText={`No employees match these filters for ${range.label}.`} contentKey={`${view}-${data?.granularity}-${data?.columns.length}`}>
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow className="bg-surface-50">
              <TableHead className={cn(stickyName, "bg-surface-50")}><div className={NAME_WIDTH}><TableScrollStart />Employee Name</div></TableHead>
              <TableHead className={cn(stickyCode, "bg-surface-50")}><div className={CODE_WIDTH}>Emp Code</div></TableHead>
              {(data?.columns ?? []).map((col) => {
                if (byMonth) return <TableHead key={col} className="w-16 px-0.5 text-center">{MONTH_FMT.format(new Date(`${col}-01T00:00:00Z`))}</TableHead>;
                const d = new Date(`${col}T00:00:00Z`);
                const weekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
                return (
                  <TableHead key={col} className={cn("w-10 px-0.5 text-center leading-tight", weekend && "text-surface-400")}>
                    <div className="text-[12px] text-surface-800">{d.getUTCDate()}</div>
                    <div className="text-[9px]">{DOW[d.getUTCDay()]}</div>
                  </TableHead>
                );
              })}
              {view === "attendance" ? (
                <>
                  <TableHead className="border-l border-surface-200 px-2 text-center">Present</TableHead>
                  <TableHead className="px-2 text-center" title="Present days in weeks the manager approved">Approved</TableHead>
                  <TableHead className="px-2 text-center" title="Present days in weeks not approved yet">Not Approved</TableHead>
                  <TableHead className="px-2 text-center">Absent</TableHead>
                  <TableHead className="px-2 text-center">Leave</TableHead>
                </>
              ) : (
                <>
                  <TableHead className="border-l border-surface-200 px-2 text-center">Total Hrs</TableHead>
                  <TableHead className="px-2 text-center">Present</TableHead>
                  <TableHead className="px-2 text-center">Avg / Day</TableHead>
                  <TableHead className="px-2 text-center">Short</TableHead>
                  <TableHead className="px-2 text-center">Absent</TableHead>
                  <TableHead className="px-2 text-center">Leave</TableHead>
                  <TableHead className="px-2 text-center">No Out</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {view === "attendance"
              ? rows.map((r) => <AttendanceRow key={r.id} row={r} byMonth={byMonth} />)
              : rows.map((r) => <HoursRow key={r.id} row={r} byMonth={byMonth} />)}
          </TableBody>
        </Table>
      </ReportTableFrame>

      <ReportFooter
        meta={data?.meta} onPage={(p) => setF({ page: String(p) })}
      />
    </ReportPage>
  );
}
